
import { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, ChevronDown, ChevronUp, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import { ShotHistoryRecord } from "@/utils/historyUtils";
import { formatCoordinates, formatTimestamp } from "@/utils/golfUtils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface ShotHistoryProps {
  history: ShotHistoryRecord[];
  isLoading: boolean;
  onDelete: (id: string) => Promise<boolean>;
  refreshHistory: () => void;
}

const ShotHistory: React.FC<ShotHistoryProps> = ({
  history,
  isLoading,
  onDelete,
  refreshHistory
}) => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  
  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleDelete = async (id: string) => {
    const success = await onDelete(id);
    if (success) {
      refreshHistory();
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full mt-6">
        <CardHeader>
          <CardTitle className="text-center">{t("ballDistance.history.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center">
          <p className="text-muted-foreground">{t("ballDistance.history.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Card className="w-full mt-6">
        <CardHeader>
          <CardTitle className="text-center">{t("ballDistance.history.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center">
          <p className="text-muted-foreground">{t("ballDistance.history.noShots")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-center">{t("ballDistance.history.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Button 
          variant="outline" 
          size="sm" 
          className="mb-4 w-full"
          onClick={refreshHistory}
        >
          {t("ballDistance.history.refresh")}
        </Button>
        
        <div className="space-y-2">
          {history.map((shot) => (
            <Collapsible 
              key={shot.id} 
              open={openItems[shot.id]} 
              onOpenChange={() => toggleItem(shot.id)}
              className="border rounded-md"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50">
                <div className="flex items-center">
                  <div className="mr-2">
                    {openItems[shot.id] ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium">
                      {format(new Date(shot.created_at), "d MMMM yyyy", { locale: fr })}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      {shot.distance.toFixed(1)}m
                      {shot.tendency === 'left' && <ArrowLeft className="h-3 w-3" />}
                      {shot.tendency === 'right' && <ArrowRight className="h-3 w-3" />}
                      {shot.club && (
                        <span className="ml-2 px-2 py-0.5 bg-muted rounded text-xs">
                          {shot.club}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {format(new Date(shot.created_at), "HH:mm", { locale: fr })}
                </div>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="p-3 pt-0 border-t">
                <div className="mt-3 space-y-3">
                  {/* Start position */}
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-green-600 mt-1" />
                    <div>
                      <p className="text-sm font-medium">{t("ballDistance.history.startPosition")}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatCoordinates(shot.start_position)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(shot.start_position.timestamp)}
                      </p>
                    </div>
                  </div>
                  
                  {/* End position */}
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-red-600 mt-1" />
                    <div>
                      <p className="text-sm font-medium">{t("ballDistance.history.endPosition")}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatCoordinates(shot.end_position)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(shot.end_position.timestamp)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Distance and Tendency */}
                  <div className="mt-2">
                    <p className="text-sm font-medium">{t("ballDistance.history.distance")}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-bold">{shot.distance.toFixed(1)} m</p>
                      {shot.tendency && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          {shot.tendency === 'left' && (
                            <>
                              <ArrowLeft className="h-4 w-4" />
                              <span className="text-sm">Gauche</span>
                            </>
                          )}
                          {shot.tendency === 'right' && (
                            <>
                              <ArrowRight className="h-4 w-4" />
                              <span className="text-sm">Droite</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Club and Notes */}
                  {(shot.club || shot.notes) && (
                    <div className="mt-2 space-y-2">
                      {shot.club && (
                        <div>
                          <p className="text-sm font-medium">Club</p>
                          <p className="text-sm text-muted-foreground">{shot.club}</p>
                        </div>
                      )}
                      {shot.notes && (
                        <div>
                          <p className="text-sm font-medium">Observations</p>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{shot.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Delete button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2 hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => handleDelete(shot.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t("ballDistance.history.delete")}
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ShotHistory;
